import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  CopyButton,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  PasswordInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconCopy, IconLock, IconShare, IconTrash, IconUserCheck } from "@tabler/icons-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { extractErrorMessage } from "../../lib/api/errors"
import { fetchVisualReports } from "../../lib/api/meetings"
import { createMeetingShare, listMeetingShares, revokeMeetingShare, type TShareAccessMode } from "../../lib/api/shares"
import type { IProps } from "./interfaces"

const SUMMARY_VALUE = "summary"

/**
 * ShareMeetingMenu — create and manage public share links for a meeting.
 *
 * A member with access to the meeting can mint a read-only link to the summary or
 * a generated report, with an expiry (bounded by the org cap) and an optional
 * password. The full link is shown exactly ONCE (only its hash is stored), so the
 * UI surfaces it prominently with a copy button and a "won't be shown again" note.
 * Existing links list with their metadata + a revoke action — never a recoverable
 * link (there is nothing to recover). No cost/usage surfaces (tenant rule).
 */
export const ShareMeetingMenu = ({ meetingId, companyId }: IProps): React.JSX.Element => {
  const [opened, setOpened] = useState(false)
  const [kindValue, setKindValue] = useState<string>(SUMMARY_VALUE)
  const [expiryDays, setExpiryDays] = useState<number | string>(7)
  const [accessMode, setAccessMode] = useState<TShareAccessMode>("public")
  const [password, setPassword] = useState("")
  const [createdUrl, setCreatedUrl] = useState<string | null>(null)
  const [revokingId, setRevokingId] = useState<number | null>(null)

  const company = useCompanyApi()
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const sharesQuery = useQuery({
    queryKey: ["meeting-shares", companyId, meetingId],
    queryFn: () => {
      if (!company) throw new Error("No active company")
      return listMeetingShares(company, meetingId)
    },
    enabled: opened && !!company,
  })

  const reportsQuery = useQuery({
    queryKey: ["meeting-visual-reports", companyId, meetingId],
    queryFn: () => {
      if (!company) throw new Error("No active company")
      return fetchVisualReports(company, meetingId)
    },
    enabled: opened && !!company,
  })

  const maxExpiry = sharesQuery.data?.maxExpiryDays ?? 90

  const kindOptions = useMemo(() => {
    const options = [{ value: SUMMARY_VALUE, label: i18n._(msg`Meeting summary`) }]
    for (const report of reportsQuery.data ?? []) {
      options.push({ value: `report:${report.id}`, label: report.meta.title || i18n._(msg`Report`) })
    }
    return options
  }, [reportsQuery.data, i18n])

  const invalidateShares = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["meeting-shares", companyId, meetingId] })
  }

  const createMutation = useMutation({
    mutationFn: () => {
      if (!company) throw new Error("No active company")
      const days = Math.max(1, Math.min(Number(expiryDays) || 7, maxExpiry))
      const base = kindValue.startsWith("report:")
        ? { kind: "report" as const, artifactId: Number(kindValue.slice("report:".length)) }
        : { kind: "summary" as const }
      return createMeetingShare(company, meetingId, {
        ...base,
        accessMode,
        expiresInDays: days,
        password: password.length > 0 ? password : undefined,
      })
    },
    onSuccess: ({ token }) => {
      setCreatedUrl(`${window.location.origin}/share/${token}`)
      setPassword("")
      invalidateShares()
    },
    onError: (err) => {
      notifications.show({ color: "danger", message: extractErrorMessage(err) })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (shareId: number) => {
      if (!company) throw new Error("No active company")
      setRevokingId(shareId)
      return revokeMeetingShare(company, shareId)
    },
    onSuccess: () => {
      invalidateShares()
      notifications.show({ color: "success", message: i18n._(msg`Link turned off.`) })
    },
    onError: (err) => {
      notifications.show({ color: "danger", message: extractErrorMessage(err) })
    },
    onSettled: () => setRevokingId(null),
  })

  const closeModal = (): void => {
    setOpened(false)
    setCreatedUrl(null)
  }

  const shares = sharesQuery.data?.shares ?? []

  return (
    <>
      <Button variant="default" leftSection={<IconShare size={16} />} onClick={() => setOpened(true)}>
        <Trans>Share</Trans>
      </Button>

      <Modal opened={opened} onClose={closeModal} title={<Trans>Share this meeting</Trans>} size="lg" centered>
        <Stack>
          {createdUrl ? (
            <Stack gap="xs">
              <Alert color="brand" variant="light">
                <Trans>Copy this link now — for security it won't be shown again.</Trans>
              </Alert>
              <Group gap="xs" wrap="nowrap">
                <TextInput value={createdUrl} readOnly style={{ flex: 1 }} aria-label={i18n._(msg`Share link`)} />
                <CopyButton value={createdUrl}>
                  {({ copied, copy }) => (
                    <Button color={copied ? "success" : undefined} onClick={copy} leftSection={<IconCopy size={16} />}>
                      {copied ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Button variant="subtle" onClick={() => setCreatedUrl(null)}>
                <Trans>Create another link</Trans>
              </Button>
            </Stack>
          ) : (
            <Stack gap="sm">
              <Select
                label={i18n._(msg`What to share`)}
                data={kindOptions}
                value={kindValue}
                onChange={(value) => setKindValue(value ?? SUMMARY_VALUE)}
                allowDeselect={false}
              />
              <div>
                <Text size="sm" fw={500} mb={4}>
                  <Trans>Who can open it</Trans>
                </Text>
                <SegmentedControl
                  fullWidth
                  value={accessMode}
                  onChange={(value) => setAccessMode(value === "authenticated" ? "authenticated" : "public")}
                  data={[
                    { value: "public", label: i18n._(msg`Anyone with the link`) },
                    { value: "authenticated", label: i18n._(msg`Signed-in members only`) },
                  ]}
                />
              </div>
              <NumberInput
                label={i18n._(msg`Expires after (days)`)}
                description={i18n._(msg`Up to ${maxExpiry} days`)}
                value={expiryDays}
                onChange={setExpiryDays}
                min={1}
                max={maxExpiry}
                clampBehavior="strict"
              />
              <PasswordInput
                label={i18n._(msg`Password (optional)`)}
                description={i18n._(msg`Viewers must enter this to open the link.`)}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
              />
              <Button
                onClick={() => createMutation.mutate()}
                loading={createMutation.isPending}
                leftSection={<IconShare size={16} />}
              >
                <Trans>Create link</Trans>
              </Button>
            </Stack>
          )}

          <Divider label={<Trans>Active links</Trans>} labelPosition="center" />

          {sharesQuery.isLoading ? (
            <Group justify="center" py="sm">
              <Loader size="sm" />
            </Group>
          ) : shares.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="sm">
              <Trans>No active links yet.</Trans>
            </Text>
          ) : (
            <Stack gap="xs">
              {shares.map((share) => (
                <Group key={share.id} justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    <Badge variant="light" color={share.kind === "report" ? "info" : "brand"}>
                      {share.kind === "report" ? <Trans>Report</Trans> : <Trans>Summary</Trans>}
                    </Badge>
                    {share.accessMode === "authenticated" ? (
                      <Tooltip label={i18n._(msg`Signed-in members only`)}>
                        <IconUserCheck size={14} />
                      </Tooltip>
                    ) : null}
                    {share.hasPassword ? (
                      <Tooltip label={i18n._(msg`Password protected`)}>
                        <IconLock size={14} />
                      </Tooltip>
                    ) : null}
                    <Text size="xs" c="dimmed">
                      <Trans>
                        expires {new Date(share.expiresAt).toLocaleDateString()} · {share.viewCount} views
                      </Trans>
                    </Text>
                  </Group>
                  <Tooltip label={i18n._(msg`Turn off link`)}>
                    <ActionIcon
                      variant="subtle"
                      color="danger"
                      loading={revokingId === share.id}
                      onClick={() => revokeMutation.mutate(share.id)}
                      aria-label={i18n._(msg`Turn off link`)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      </Modal>
    </>
  )
}
